import debounce from "lodash/debounce";
import { useEffect, useMemo, useRef, useState } from "react";
import type {
  ControlProps,
  GroupBase,
  OptionProps,
  StylesConfig,
  ValueContainerProps,
} from "react-select";
import { components as ReactSelectComponents, components, mergeStyles } from "react-select";
import AsyncSelect from "react-select/async";

import ExternalLinkIcon from "@assets/icons/external-link.svg";
import LineDashedIcon from "@assets/icons/line-dashed.svg";

import { APP_COLORS } from "@styles/colors";
import { cn } from "@utils/tailwind.util";
import { ICompany } from "@interfaces/company.interface";
import { CompanyTypeSubtype } from "@enums/companies.enum";
import { CompanyIconBadge } from "@components/AddNewEntity/Shared/CompanyIconBadge";
import { useMaxMenuHeight } from "@components/AddNewEntity/hooks/useMaxMenuHeight";
import useSystemStore from "@store/useSystemStore";
import { useSearchCompanies } from "@components/AddNewEntity/hooks/useSearchCompanies";
import {
  ClearIndicator,
  DropdownIndicator,
  getCustomSelectStyles,
  type SelectProps,
} from "@ui/Select";

export interface IPlatformCompanySelectOption {
  value: string;
  label: string;
  isDisabled?: boolean | null;
  company: Partial<ICompany>;
}

const getCustomStyles = (
  maxMenuHeight: number
): StylesConfig<IPlatformCompanySelectOption, false> => {
  const { isDarkMode } = useSystemStore();

  return {
    control: (provided, { isFocused, menuIsOpen }) => ({
      ...provided,
      borderWidth: 1,
      borderColor:
        isFocused || menuIsOpen
          ? isDarkMode
            ? APP_COLORS.stroke.actionPrimaryDark
            : APP_COLORS.stroke.actionPrimary
          : isDarkMode
            ? APP_COLORS.stroke.primaryDark
            : APP_COLORS.stroke.primary,
      backgroundColor: isDarkMode
        ? APP_COLORS.background.primaryDark
        : APP_COLORS.background.primary,
      color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
      boxShadow: isFocused
        ? "0px 0px 0px 3px #B9D6D1" // theme.boxShadow.mainButtonFocus
        : "none",
      ":hover": {
        borderColor: isDarkMode ? APP_COLORS.stroke.secondaryDark : APP_COLORS.stroke.secondary,
      },
      ":focus": {
        borderColor: isDarkMode
          ? APP_COLORS.stroke.actionPrimaryDark
          : APP_COLORS.stroke.actionPrimary,
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
    }),
    menu: (provided) => ({
      ...provided,
      maxHeight: maxMenuHeight,
      borderWidth: 1,
      borderRadius: 8,
      borderColor: isDarkMode ? APP_COLORS.stroke.primaryDark : APP_COLORS.stroke.primary,
      backgroundColor: isDarkMode
        ? APP_COLORS.background.primaryDark
        : APP_COLORS.background.primary,
    }),
    option: (provided, { isSelected, isFocused }) => ({
      ...provided,
      backgroundColor: isSelected
        ? isDarkMode
          ? APP_COLORS.background.primaryDark
          : APP_COLORS.background.primary
        : isFocused
          ? isDarkMode
            ? APP_COLORS.background.primaryDark
            : APP_COLORS.background.primary
          : "transparent",
      color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
      ":hover": {
        backgroundColor: isDarkMode
          ? APP_COLORS.background.secondaryDark
          : APP_COLORS.background.secondary,
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: APP_COLORS.gray["400"],
    }),
    indicatorSeparator: () => ({ display: "none" }),
    input: (provided) => ({
      ...provided,
      color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: maxMenuHeight,
    }),
  };
};

const CustomOption = (
  props: OptionProps<IPlatformCompanySelectOption, false, GroupBase<IPlatformCompanySelectOption>>
) => {
  const { data } = props;

  return (
    <components.Option {...props} className="!p-0">
      <div className="flex items-center px-3 py-2 gap-2 cursor-pointer min-h-[44px]">
        {data.value === "" ? (
          <LineDashedIcon width={20} height={20} color={APP_COLORS.content.disabled} />
        ) : (
          <div className={data.isDisabled ? "opacity-40" : ""}>
            <CompanyIconBadge url={data.company.logo_url} />
          </div>
        )}
        <div className="flex flex-col min-w-0 flex-1">
          <span
            className={cn("font-medium text-sm truncate", {
              "text-content-secondary": data.isDisabled,
            })}
          >
            {data.label}
          </span>
          {data.company.website && (
            <span
              className={cn("text-xs truncate", {
                "text-content-secondary": data.isDisabled,
              })}
            >
              {data.company.website.replace(/^(https?:\/\/)?(www\.)?/, "").replace(/\/$/, "")}
            </span>
          )}
        </div>
        {data.company.website && (
          <a
            href={data.company.website}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              data.company.website && window.open(data.company.website, "_blank");
            }}
          >
            <div className="internal-link group flex h-5 w-5 items-center justify-center rounded-md opacity-0 hover:bg-[#E5E7EB] group-hover:opacity-100">
              <ExternalLinkIcon className="scale-75 text-content-secondary group-[.internal-link]:hover:text-content-primary" />
            </div>
          </a>
        )}
      </div>
    </components.Option>
  );
};

const Control = ({
  children,
  ...props
}: ControlProps<IPlatformCompanySelectOption, false, GroupBase<IPlatformCompanySelectOption>>) => {
  const value = props.getValue();
  const showLogo = props.hasValue && value.length > 0;

  return (
    <components.Control {...props}>
      {value[0]?.value === "" ? (
        <div className="ml-2">
          <LineDashedIcon width={20} height={20} color={APP_COLORS.content.disabled} />
        </div>
      ) : (
        showLogo && (
          <div className="ml-2">
            <CompanyIconBadge url={value[0]?.company.logo_url} />
          </div>
        )
      )}
      {children}
    </components.Control>
  );
};

export const unassignedOption: IPlatformCompanySelectOption = {
  value: "",
  label: "No company",
  company: {
    id: "",
    title: "",
    logo_url: "",
    website: "",
  },
};

export function PlatformCompanySelect({
  excludeCompanies = [],
  ...rest
}: SelectProps<IPlatformCompanySelectOption, false> & { excludeCompanies?: string[] }) {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const callbackRef = useRef<any>(null);

  const dropdownButtonRef = useRef<HTMLDivElement>(null);
  const maxMenuHeight = useMaxMenuHeight(dropdownButtonRef);

  const {
    data: searchedCompanies,
    isLoading: isLoadingSearchedCompanies,
    fetchStatus,
  } = useSearchCompanies({ searchQuery, currentCompanies: [] });

  const options: Array<{ label: string; options: IPlatformCompanySelectOption[] }> = useMemo(() => {
    if (!searchedCompanies) return [];

    const parsedSearchedCompanies: IPlatformCompanySelectOption[] =
      searchedCompanies
        ?.filter((company) => !excludeCompanies.includes(company.id))
        ?.map((company) => ({
          value: company.id,
          label: company.title,
          isDisabled: company.type
            ? ![CompanyTypeSubtype.PLATFORM, CompanyTypeSubtype.PORTFOLIO].includes(
                company.type.subtype!
              )
            : true,
          company,
        })) || [];

    const enabled = parsedSearchedCompanies.filter((opt) => !opt.isDisabled);
    const disabled = parsedSearchedCompanies.filter((opt) => opt.isDisabled);

    const groups = [];

    groups.push({ label: "", options: [unassignedOption, ...enabled] });
    if (disabled.length > 0) {
      groups.push({ label: "TYPE NOT ASSIGNABLE", options: disabled });
    }
    return groups;
  }, [excludeCompanies, searchedCompanies]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      setSearchQuery("");
    }
  };

  useEffect(() => {
    if (isLoadingSearchedCompanies || fetchStatus !== "idle") return;
    if (callbackRef.current) {
      if (searchQuery && options.length > 0) {
        callbackRef.current(options);
      } else {
        callbackRef.current([]);
      }
    }
  }, [fetchStatus, isLoadingSearchedCompanies, options, searchQuery]);

  const noOptionsMessage = ({ inputValue }: { inputValue: string }) =>
    inputValue.length > 0 ? (
      <span className="text-sm">No companies found with this name.</span>
    ) : (
      "No companies"
    );

  const debouncedSearch = useMemo(
    () =>
      debounce((inputValue: string) => {
        setSearchQuery(inputValue);
      }, 1000),
    []
  );

  const loadOptions = (_: string, callback: (arr: Array<IPlatformCompanySelectOption>) => void) => {
    callbackRef.current = callback;
  };

  return (
    <div ref={dropdownButtonRef}>
      <AsyncSelect<IPlatformCompanySelectOption, false>
        {...rest}
        value={rest.value || unassignedOption}
        components={{
          Option: CustomOption,
          Control,
          DropdownIndicator,
          ClearIndicator,
          ...rest.components,
        }}
        isMulti={false}
        styles={
          rest.styles ||
          mergeStyles(
            rest.styles || {},
            mergeStyles(getCustomSelectStyles(), getCustomStyles(maxMenuHeight))
          )
        }
        cacheOptions
        options={options}
        menuPosition="absolute"
        noOptionsMessage={noOptionsMessage}
        isClearable={true}
        defaultOptions={options}
        onKeyDown={handleKeyDown}
        onInputChange={(inputValue, actionMeta) => {
          if (actionMeta.action === "input-change") {
            debouncedSearch(inputValue);
          }
        }}
        loadOptions={loadOptions}
        isLoading={isLoadingSearchedCompanies}
      />
    </div>
  );
}
